/**
 * WeaveIn.ai Platform - Organization Setup Wizard API Service
 * TypeScript service layer for the hierarchical organization setup wizard.
 * Manages program affiliations, field mappings, import rules, billing, reporting,
 * staff assignments, and wizard draft persistence.
 */

import axios, { AxiosInstance } from 'axios';

import { config as productConfig } from '@/src/config';
// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

// --- Wizard Draft ---
export interface OrganizationSetupDraft {
  id: number;
  cbo_id: number;
  created_by: number;
  wizard_state: Record<string, any>;
  current_step: number;
  status: 'DRAFT' | 'IN_PROGRESS' | 'COMPLETED' | 'ABANDONED';
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

export interface DraftCreate {
  cbo_id: number;
  wizard_state?: Record<string, any>;
  current_step?: number;
}

export interface DraftUpdate {
  wizard_state?: Record<string, any>;
  current_step?: number;
  status?: 'DRAFT' | 'IN_PROGRESS' | 'COMPLETED' | 'ABANDONED';
}

// --- Program Affiliations ---
export type AffiliationType = 'MCP' | 'COMMERCIAL' | 'MEDICARE' | 'SPONSOR' | 'PARTNER';
export type AffiliationStatus = 'ACTIVE' | 'INACTIVE' | 'PENDING';

export interface ProgramAffiliation {
  id: number;
  program_id: number;
  payer_id?: number;
  health_plan_id?: number;
  cbo_id: number;
  affiliation_type: AffiliationType;
  contract_number?: string;
  effective_date?: string;
  termination_date?: string;
  status: AffiliationStatus;
  mcp_program_code?: string;
  mcp_provider_id?: string;
  settings?: Record<string, any>;
  created_at?: string;
  // Joined fields
  payer_name?: string;
  program_name?: string;
  health_plan_name?: string;
}

export interface ProgramAffiliationCreate {
  payer_id?: number;
  health_plan_id?: number;
  affiliation_type: AffiliationType;
  contract_number?: string;
  effective_date?: string;
  termination_date?: string;
  status?: AffiliationStatus;
  mcp_program_code?: string;
  mcp_provider_id?: string;
  settings?: Record<string, any>;
}

// --- Field Mappings ---
export interface AffiliationFieldMapping {
  id?: number;
  affiliation_id: number;
  db_column: string;
  source_field_name: string;
  friendly_display_name?: string;
  semantic_group?: string;
  transform_type?: string;
  transform_config?: Record<string, any>;
  is_required: boolean;
  validation_regex?: string;
  validation_message?: string;
  is_mcp_recommended: boolean;
  mcp_display_only: boolean;
  sort_order?: number;
  is_active: boolean;
}

export interface FieldMappingBulkUpdate {
  mappings: AffiliationFieldMapping[];
}

export interface FieldDetectionResult {
  source_field: string;
  db_column: string;
  semantic_group: string;
  display_name: string;
  confidence: number;
  match_reason: 'exact_alias' | 'fuzzy_alias' | 'payer_recommended' | 'import_mapping' | 'unrecognized';
}

// --- Import Rules ---
export type DuplicateAction = 'FLAG' | 'SKIP' | 'MERGE' | 'UPDATE';
export type UpdateStrategy = 'UPDATE_ALL' | 'UPDATE_EMPTY_ONLY' | 'MANUAL_REVIEW';

export interface AffiliationImportRules {
  id?: number;
  affiliation_id: number;
  duplicate_detection_fields: string[];
  duplicate_match_threshold: number;
  duplicate_primary_field: string;
  duplicate_action: DuplicateAction;
  protected_fields: string[];
  update_strategy: UpdateStrategy;
  error_notification_emails: string[];
  max_error_rate_percent: number;
}

// --- Report Configs ---
export type ReportType = 'ENROLLMENT' | 'UTILIZATION' | 'OUTCOMES' | 'BILLING' | 'CUSTOM';
export type OutputFormat = 'CSV' | 'XLSX' | 'PDF' | 'EDI';
export type ReportFrequency = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'ON_DEMAND';
export type DeliveryMethod = 'SFTP' | 'EMAIL' | 'PORTAL' | 'API';

export interface AffiliationReportConfig {
  id?: number;
  affiliation_id: number;
  report_name: string;
  report_type: ReportType;
  included_fields: string[];
  field_order: string[];
  field_display_names: Record<string, string>;
  output_format: OutputFormat;
  frequency: ReportFrequency;
  delivery_method: DeliveryMethod;
  delivery_config?: Record<string, any>;
  is_active: boolean;
  auto_generate: boolean;
  payer_name?: string;
  program_name?: string;
}

// --- Billing Config ---
export type BillingModel = 'PMPM' | 'FEE_FOR_SERVICE' | 'CAPITATED' | 'HYBRID';
export type ClaimFormat = '837P' | '837I' | 'CUSTOM_CSV';

export interface AffiliationBillingConfig {
  id?: number;
  affiliation_id: number;
  billing_model: BillingModel;
  service_codes: Array<{ code: string; description: string; rate: number; unit: string }>;
  claim_format: ClaimFormat;
  payer_specific_id?: string;
  submission_config?: Record<string, any>;
}

// --- Staff Assignments ---
export type ProgramRole = 'lead' | 'coordinator' | 'specialist' | 'viewer';

export interface StaffProgramAssignment {
  id?: number;
  user_id: number;
  cbo_id: number;
  program_id: number;
  affiliation_id?: number;
  role_in_program: ProgramRole;
  is_primary: boolean;
  max_caseload?: number;
  current_caseload?: number;
  effective_date?: string;
  end_date?: string;
  // Joined fields
  user_name?: string;
  program_name?: string;
  affiliation_name?: string;
}

export interface StaffAssignmentCreate {
  user_id: number;
  program_id: number;
  affiliation_id?: number;
  role_in_program: ProgramRole;
  is_primary?: boolean;
  max_caseload?: number;
  effective_date?: string;
}

// --- Semantic Fields ---
export interface SemanticFieldGroup {
  group_key: string;
  db_column: string;
  display_name: string;
  known_aliases: string[];
  description?: string;
}

export interface SemanticGroupDetail extends SemanticFieldGroup {
  payer_usage: Array<{ payer_id: number; payer_name: string; field_name: string }>;
}

// --- Record Model ---
export type RecordModel = 'STANDARD' | 'HOUSEHOLD' | 'EPISODE';

export interface RecordModelConfig {
  record_model: RecordModel;
  program_type: string;
  program_name: string;
}

// --- New Case Rules ---
export type DetectionType = 'FIELD_CHANGE' | 'DATE_GAP' | 'STATUS_CHANGE' | 'MANUAL';
export type DetectionAction = 'AUTO_CREATE' | 'PROMPT_USER' | 'FLAG_FOR_REVIEW';

export interface NewCaseRule {
  id?: number;
  program_id?: number;
  program_type?: string;
  cbo_id?: number;
  rule_name: string;
  rule_description?: string;
  detection_type: DetectionType;
  detection_config: Record<string, any>;
  on_detection_action: DetectionAction;
  auto_archive_previous: boolean;
  is_active: boolean;
  sort_order?: number;
}

// --- Recommended Fields ---
export interface RecommendedField {
  mcp_field: string;
  db_column: string;
  semantic_group: string;
  is_required: boolean;
  description?: string;
}

// ============================================================================
// API CLIENT
// ============================================================================

class OrganizationSetupAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (cboId) {
        config.headers['X-CBO-ID'] = cboId;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('OrganizationSetup API Error:', {
          status: error.response?.status,
          url: error.config?.url,
          method: error.config?.method?.toUpperCase(),
          message: error.response?.data?.detail || error.message,
        });
        return Promise.reject(error);
      }
    );
  }

  // ==========================================================================
  // WIZARD DRAFTS
  // ==========================================================================

  async createDraft(data: DraftCreate): Promise<OrganizationSetupDraft> {
    const res = await this.client.post('/api/organization-setup/drafts', data);
    return res.data;
  }

  async listDrafts(): Promise<OrganizationSetupDraft[]> {
    const res = await this.client.get('/api/organization-setup/drafts');
    return res.data;
  }

  async getDraft(draftId: number): Promise<OrganizationSetupDraft> {
    const res = await this.client.get(`/api/organization-setup/drafts/${draftId}`);
    return res.data;
  }

  async updateDraft(draftId: number, data: DraftUpdate): Promise<{ status: string }> {
    const res = await this.client.put(`/api/organization-setup/drafts/${draftId}`, data);
    return res.data;
  }

  async deleteDraft(draftId: number): Promise<{ status: string }> {
    const res = await this.client.delete(`/api/organization-setup/drafts/${draftId}`);
    return res.data;
  }

  // ==========================================================================
  // PROGRAM AFFILIATIONS
  // ==========================================================================

  async createAffiliation(
    cboId: number,
    programId: number,
    data: ProgramAffiliationCreate
  ): Promise<ProgramAffiliation> {
    const res = await this.client.post(
      `/api/organization-setup/${cboId}/programs/${programId}/affiliations`,
      data
    );
    return res.data;
  }

  async listAffiliations(cboId: number, programId: number): Promise<ProgramAffiliation[]> {
    const res = await this.client.get(
      `/api/organization-setup/${cboId}/programs/${programId}/affiliations`
    );
    return res.data;
  }

  async listAllAffiliations(cboId: number): Promise<ProgramAffiliation[]> {
    const res = await this.client.get(
      `/api/organization-setup/${cboId}/affiliations`
    );
    return res.data;
  }

  async updateAffiliation(
    affiliationId: number,
    data: Partial<ProgramAffiliationCreate>
  ): Promise<{ status: string }> {
    const res = await this.client.put(
      `/api/organization-setup/affiliations/${affiliationId}`,
      data
    );
    return res.data;
  }

  async deleteAffiliation(affiliationId: number): Promise<{ status: string }> {
    const res = await this.client.delete(
      `/api/organization-setup/affiliations/${affiliationId}`
    );
    return res.data;
  }

  // ==========================================================================
  // FIELD MAPPINGS
  // ==========================================================================

  async getFieldMappings(affiliationId: number): Promise<AffiliationFieldMapping[]> {
    const res = await this.client.get(
      `/api/organization-setup/affiliations/${affiliationId}/field-mappings`
    );
    return res.data;
  }

  async updateFieldMappings(
    affiliationId: number,
    data: FieldMappingBulkUpdate
  ): Promise<{ status: string; count: number }> {
    const res = await this.client.put(
      `/api/organization-setup/affiliations/${affiliationId}/field-mappings`,
      data
    );
    return res.data;
  }

  async detectMappings(
    affiliationId: number,
    sourceFields: string[]
  ): Promise<FieldDetectionResult[]> {
    const res = await this.client.post(
      `/api/organization-setup/affiliations/${affiliationId}/detect-mappings`,
      { source_fields: sourceFields }
    );
    return res.data;
  }

  async getRecommendedFields(affiliationId: number): Promise<RecommendedField[]> {
    const res = await this.client.get(
      `/api/organization-setup/affiliations/${affiliationId}/recommended-fields`
    );
    return res.data;
  }

  // ==========================================================================
  // IMPORT RULES
  // ==========================================================================

  async getImportRules(affiliationId: number): Promise<AffiliationImportRules> {
    const res = await this.client.get(
      `/api/organization-setup/affiliations/${affiliationId}/import-rules`
    );
    return res.data;
  }

  async updateImportRules(
    affiliationId: number,
    data: Partial<AffiliationImportRules>
  ): Promise<{ status: string }> {
    const res = await this.client.put(
      `/api/organization-setup/affiliations/${affiliationId}/import-rules`,
      data
    );
    return res.data;
  }

  // ==========================================================================
  // REPORT CONFIGS
  // ==========================================================================

  async listReportConfigs(affiliationId: number): Promise<AffiliationReportConfig[]> {
    const res = await this.client.get(
      `/api/organization-setup/affiliations/${affiliationId}/report-configs`
    );
    return res.data;
  }

  async createReportConfig(
    affiliationId: number,
    data: Omit<AffiliationReportConfig, 'id' | 'affiliation_id'>
  ): Promise<AffiliationReportConfig> {
    const res = await this.client.post(
      `/api/organization-setup/affiliations/${affiliationId}/report-configs`,
      data
    );
    return res.data;
  }

  async updateReportConfig(
    configId: number,
    data: Partial<AffiliationReportConfig>
  ): Promise<{ status: string }> {
    const res = await this.client.put(
      `/api/organization-setup/report-configs/${configId}`,
      data
    );
    return res.data;
  }

  async deleteReportConfig(configId: number): Promise<{ status: string }> {
    const res = await this.client.delete(
      `/api/organization-setup/report-configs/${configId}`
    );
    return res.data;
  }

  // ==========================================================================
  // BILLING CONFIG
  // ==========================================================================

  async getBillingConfig(affiliationId: number): Promise<AffiliationBillingConfig> {
    const res = await this.client.get(
      `/api/organization-setup/affiliations/${affiliationId}/billing-config`
    );
    return res.data;
  }

  async updateBillingConfig(
    affiliationId: number,
    data: Partial<AffiliationBillingConfig>
  ): Promise<{ status: string }> {
    const res = await this.client.put(
      `/api/organization-setup/affiliations/${affiliationId}/billing-config`,
      data
    );
    return res.data;
  }

  // ==========================================================================
  // SEMANTIC FIELD GROUPS
  // ==========================================================================

  async listSemanticGroups(): Promise<SemanticFieldGroup[]> {
    const res = await this.client.get('/api/organization-setup/semantic-groups');
    return res.data;
  }

  async getSemanticGroupDetail(groupKey: string): Promise<SemanticGroupDetail> {
    const res = await this.client.get(
      `/api/organization-setup/semantic-groups/${groupKey}/suggestions`
    );
    return res.data;
  }

  // ==========================================================================
  // STAFF ASSIGNMENTS
  // ==========================================================================

  async listOrgStaff(cboId: number): Promise<any[]> {
    const res = await this.client.get(`/api/organization-setup/${cboId}/staff`);
    return res.data;
  }

  async createStaffAssignment(
    cboId: number,
    data: StaffAssignmentCreate
  ): Promise<StaffProgramAssignment> {
    const res = await this.client.post(
      `/api/organization-setup/${cboId}/staff-assignments`,
      data
    );
    return res.data;
  }

  async listStaffAssignments(
    cboId: number,
    programId: number
  ): Promise<StaffProgramAssignment[]> {
    const res = await this.client.get(
      `/api/organization-setup/${cboId}/programs/${programId}/staff-assignments`
    );
    return res.data;
  }

  async updateStaffAssignment(
    assignmentId: number,
    data: Partial<StaffAssignmentCreate>
  ): Promise<{ status: string }> {
    const res = await this.client.put(
      `/api/organization-setup/staff-assignments/${assignmentId}`,
      data
    );
    return res.data;
  }

  async deleteStaffAssignment(assignmentId: number): Promise<{ status: string }> {
    const res = await this.client.delete(
      `/api/organization-setup/staff-assignments/${assignmentId}`
    );
    return res.data;
  }

  // ==========================================================================
  // RECORD MODEL CONFIG
  // ==========================================================================

  async getRecordModel(cboId: number, programId: number): Promise<RecordModelConfig> {
    const res = await this.client.get(
      `/api/organization-setup/${cboId}/programs/${programId}/record-model`
    );
    return res.data;
  }

  async updateRecordModel(
    cboId: number,
    programId: number,
    data: { record_model: RecordModel }
  ): Promise<{ status: string }> {
    const res = await this.client.put(
      `/api/organization-setup/${cboId}/programs/${programId}/record-model`,
      data
    );
    return res.data;
  }

  // ==========================================================================
  // NEW CASE RULES
  // ==========================================================================

  async listNewCaseRules(cboId: number, programId: number): Promise<NewCaseRule[]> {
    const res = await this.client.get(
      `/api/organization-setup/${cboId}/programs/${programId}/new-case-rules`
    );
    return res.data;
  }

  async createNewCaseRule(
    cboId: number,
    programId: number,
    data: Omit<NewCaseRule, 'id'>
  ): Promise<NewCaseRule> {
    const res = await this.client.post(
      `/api/organization-setup/${cboId}/programs/${programId}/new-case-rules`,
      data
    );
    return res.data;
  }

  async updateNewCaseRule(
    ruleId: number,
    data: Partial<NewCaseRule>
  ): Promise<{ status: string }> {
    const res = await this.client.put(
      `/api/organization-setup/new-case-rules/${ruleId}`,
      data
    );
    return res.data;
  }

  async deleteNewCaseRule(ruleId: number): Promise<{ status: string }> {
    const res = await this.client.delete(
      `/api/organization-setup/new-case-rules/${ruleId}`
    );
    return res.data;
  }

  // ==========================================================================
  // ACTIVATION
  // ==========================================================================

  async activateOrganization(cboId: number): Promise<{ status: string; message: string }> {
    const res = await this.client.post(`/api/organization-setup/${cboId}/activate`);
    return res.data;
  }
}

// Singleton export
export const organizationSetupAPI = new OrganizationSetupAPI();
